/**
 * Tasks API Service
 * All API calls for tasks management
 * FSM v2.0 - 6 estados + 23 sub-estados
 */

import { apiClient } from './client';
import type { ApiResponse, PaginatedResponse } from './types';
import { TaskStatus, TaskSubStatus, CommissionStatus } from '../../types/task.types';
import type {
  Task,
  TaskCreateInput,
  TaskUpdateInput,
  TaskFilters,
  AvailableTransitionsResponse,
  GlobalTasksStats,
} from '../../types/task.types';
import {
  mockGetTasks,
  mockGetTask,
  mockCreateTask,
  mockUpdateTask,
  mockDeleteTask,
} from '../../services/mockTasksService';

// Check if mock mode is enabled
const USE_MOCK_DATA = import.meta.env['VITE_USE_MOCK_DATA'] === 'true';

/**
 * Tasks API Endpoints
 */
export const tasksApi = {
  /**
   * List tasks with filters and pagination
   * GET /api/tasks
   */
  async list(filters?: TaskFilters): Promise<PaginatedResponse<Task>> {
    if (USE_MOCK_DATA) {
      const tasks = await mockGetTasks();
      // Simula paginación básica
      return {
        success: true,
        data: tasks,
        meta: {
          page: filters?.page || 1,
          limit: filters?.limit || 20,
          totalItems: tasks.length,
          totalPages: 1,
          hasNextPage: false,
          hasPreviousPage: false,
        },
      };
    }
    const params = new URLSearchParams();

    if (filters?.page) params.append('page', filters.page.toString());
    if (filters?.limit) params.append('limit', filters.limit.toString());
    if (filters?.doctorId) params.append('doctorId', filters.doctorId);

    // Helper to append single value or array
    const appendFilter = (key: string, value: string | string[] | undefined) => {
      if (!value) return;
      if (Array.isArray(value)) {
        value.forEach((v) => params.append(key, v));
      } else {
        params.append(key, value);
      }
    };

    // Handle filters that support arrays
    appendFilter('insurerId', filters?.insurerId);
    appendFilter('status', filters?.status);
    appendFilter('subStatus', filters?.subStatus);
    appendFilter('commissionStatus', filters?.commissionStatus);
    appendFilter('serviceType', filters?.serviceType);
    appendFilter('expectedPaymentStatus', filters?.expectedPaymentStatus);
    appendFilter('documentStatus', filters?.documentStatus);
    if (filters?.fromDate) params.append('fromDate', filters.fromDate);
    if (filters?.toDate) params.append('toDate', filters.toDate);
    if (filters?.uploadedFromDate) params.append('uploadedFromDate', filters.uploadedFromDate);
    if (filters?.uploadedToDate) params.append('uploadedToDate', filters.uploadedToDate);
    if (filters?.amountMin) params.append('amountMin', String(filters.amountMin));
    if (filters?.amountMax) params.append('amountMax', String(filters.amountMax));
    if (filters?.search) params.append('search', filters.search);
    if (filters?.sortBy) params.append('sortBy', filters.sortBy);
    if (filters?.sortOrder) params.append('sortOrder', filters.sortOrder);

    const queryString = params.toString();
    const url = queryString ? `/tasks?${queryString}` : '/tasks';

    return apiClient.get(url);
  },

  /**
   * Get global tasks statistics (counts and amounts by status)
   * GET /api/tasks/stats
   * Returns stats that are NOT affected by filters/pagination
   */
  async getGlobalStats(): Promise<ApiResponse<GlobalTasksStats>> {
    if (USE_MOCK_DATA) {
      // Mock implementation for dev
      return {
        success: true,
        data: {
          byStatus: {
            verificacion: { count: 10, amount: '50000.00' },
            en_proceso: { count: 8, amount: '75000.00' },
            detenidos: { count: 3, amount: '15000.00' },
            pagado: { count: 25, amount: '200000.00' },
            negados: { count: 2, amount: '5000.00' },
            cancelado: { count: 1, amount: '2500.00' },
          },
          totals: {
            totalTasks: 49,
            totalAmount: '347500.00',
          },
        },
      };
    }

    return apiClient.get('/tasks/stats');
  },

  /**
   * Get a single task by ID
   * GET /api/tasks/:id
   */
  async get(id: string): Promise<ApiResponse<Task>> {
    if (USE_MOCK_DATA) {
      const task = await mockGetTask(id);
      if (!task) {
        throw new Error('Task not found');
      }
      return {
        success: true,
        data: task,
      };
    }

    return apiClient.get(`/tasks/${id}`);
  },

  /**
   * Create a new task
   * POST /api/tasks
   */
  async create(data: TaskCreateInput): Promise<ApiResponse<Task>> {
    if (USE_MOCK_DATA) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const task = await mockCreateTask(data as any);
      return {
        success: true,
        data: task,
      };
    }

    return apiClient.post('/tasks', data);
  },

  /**
   * Update a task
   * PUT /api/tasks/:id
   */
  async update(id: string, data: TaskUpdateInput): Promise<ApiResponse<Task>> {
    if (USE_MOCK_DATA) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const task = await mockUpdateTask(id, data as any);
      return {
        success: true,
        data: task,
      };
    }

    return apiClient.put(`/tasks/${id}`, data);
  },

  /**
   * Update task status (FSM transition)
   * POST /api/tasks/:id/transition
   */
  async transition(
    id: string,
    toStatus: TaskStatus,
    toSubStatus?: TaskSubStatus | null,
    data?: {
      notes?: string;
      paymentAmount?: string;
      paymentReference?: string;
      paymentDate?: string;
      uploadFolio?: string; // Para transición a INGRESADA (folio de ingreso)
      uploadedToInsurerAt?: string; // Fecha de ingreso a aseguradora
      reason?: string; // Para cancelaciones y negaciones
      detainedReason?: string; // Para transición a DETENIDOS (sub-status)
      detainedDescription?: string; // Para transición a DETENIDOS (comentarios opcionales)
      invoicePdfId?: string; // Para transición a FACTURADA (ID del PDF de factura)
      invoiceXmlId?: string; // Para transición a FACTURADA (ID del XML de factura)
      invoiceUuid?: string; // Para transición a FACTURADA (UUID extraído del XML)
      complementInsurerFolio?: string; // Para transición pendiente_subir_aseguradora → finalizado
      complementUploadedToInsurerAt?: string; // Fecha de subida del complemento a aseguradora
    }
  ): Promise<ApiResponse<Task>> {
    if (USE_MOCK_DATA) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const task = await mockUpdateTask(id, { status: toStatus, subStatus: toSubStatus } as any);
      return {
        success: true,
        data: task,
      };
    }

    return apiClient.post(`/tasks-fsm/${id}/transition`, {
      targetStatus: toStatus,
      targetSubStatus: toSubStatus,
      ...data,
    });
  },

  /**
   * Detain task with specific reason
   * POST /api/tasks-fsm/:id/detain
   * Uses dedicated endpoint that properly handles detenidos transitions
   */
  async detain(
    id: string,
    subStatus: TaskSubStatus,
    description?: string
  ): Promise<ApiResponse<Task>> {
    if (USE_MOCK_DATA) {
      const task = await mockUpdateTask(id, {
        status: TaskStatus.DETENIDOS,
        subStatus,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } as any);
      return {
        success: true,
        data: task,
      };
    }

    return apiClient.post(`/tasks-fsm/${id}/detain`, {
      subStatus,
      description,
    });
  },

  /**
   * Get available transitions for a task
   * GET /api/tasks/:id/transitions
   */
  async getAvailableTransitions(id: string): Promise<ApiResponse<AvailableTransitionsResponse>> {
    if (USE_MOCK_DATA) {
      // Return mock transitions
      return {
        success: true,
        data: {
          currentStatus: 'verificacion' as TaskStatus,
          currentSubStatus: null,
          availableTransitions: [],
        },
      };
    }

    return apiClient.get(`/tasks/${id}/transitions`);
  },

  /**
   * Update task status (legacy - deprecated)
   * @deprecated Use transition() instead
   * PATCH /api/tasks/:id/status
   */
  async updateStatus(id: string, status: TaskStatus): Promise<ApiResponse<Task>> {
    if (USE_MOCK_DATA) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const task = await mockUpdateTask(id, { status } as any);
      return {
        success: true,
        data: task,
      };
    }

    return apiClient.patch(`/tasks/${id}/status`, { status });
  },

  /**
   * Delete a task (soft delete)
   * DELETE /api/tasks/:id
   */
  async delete(id: string): Promise<ApiResponse<{ message: string }>> {
    if (USE_MOCK_DATA) {
      await mockDeleteTask(id);
      return {
        success: true,
        data: { message: 'Task deleted successfully' },
      };
    }

    return apiClient.delete(`/tasks/${id}`);
  },

  /**
   * Delete ALL tasks (DEV ONLY)
   * DELETE /api/tasks/bulk/all
   */
  async deleteAll(): Promise<
    ApiResponse<{ message: string; deletedCount: number; errorCount: number; totalFound: number }>
  > {
    return apiClient.delete('/tasks/bulk/all');
  },

  /**
   * Get tasks by doctor ID
   * GET /api/tasks?doctorId=xxx
   */
  async getByDoctor(
    doctorId: string,
    filters?: Omit<TaskFilters, 'doctorId'>
  ): Promise<PaginatedResponse<Task>> {
    return this.list({ ...filters, doctorId });
  },

  /**
   * Update commission status
   * PATCH /api/tasks/:id/commission-status
   * @param id - Task ID
   * @param commissionStatus - New commission status
   * @param commissionPaidDate - Optional date when commission was paid (required for 'pagada' status)
   */
  async updateCommissionStatus(
    id: string,
    commissionStatus: string,
    commissionPaidDate?: string
  ): Promise<ApiResponse<{ id: string; commissionStatus: string; message: string }>> {
    if (USE_MOCK_DATA) {
      // Mock implementation
      return {
        success: true,
        data: { id, commissionStatus, message: 'Estado de comisión actualizado correctamente' },
      };
    }

    // El backend espera commissionStatus en español para el estado de la COMISIÓN
    // NO confundir con paymentStatus que es el pago de la aseguradora al doctor
    const payload: { commissionStatus: string; commissionPaidDate?: string } = {
      commissionStatus: commissionStatus, // Estado de la comisión: pendiente, pagada, cancelada, en_proceso
    };

    // Include commissionPaidDate if provided (typically for 'pagada' status)
    if (commissionPaidDate) {
      payload.commissionPaidDate = commissionPaidDate;
    }

    console.log('💰 Actualizando estado de COMISIÓN (Doctor → Admin):', {
      taskId: id,
      commissionStatus: commissionStatus,
      endpoint: `/tasks/${id}/commission-status`,
      nota: 'Este es el estado del pago de la comisión del doctor a Eli, NO el pago de la aseguradora',
    });

    // Llamar al endpoint
    try {
      const response = (await apiClient.patch(
        `/tasks/${id}/commission-status`,
        payload
      )) as ApiResponse<{
        id: string;
        commissionStatus: string;
        message: string;
      }>;
      console.log('✅ Respuesta del backend (commission-status):', response);
      return response;
    } catch (error) {
      console.error('❌ Error al actualizar commissionStatus:', {
        error: error instanceof Error ? error.message : 'Error desconocido',
        response: (error as { response?: { data?: unknown } }).response?.data,
        nota: 'El backend debe: 1) Aceptar commissionStatus, 2) Persistir en DB, 3) Devolver commissionStatus (no paymentStatus)',
      });
      throw error;
    }
  },

  /**
   * Get status transition history for a task
   * GET /api/tasks/:id/history
   */
  async getHistory(id: string): Promise<
    ApiResponse<{
      taskId: string;
      history: Array<{
        id: string;
        taskId: string;
        fromStatus: string | null;
        fromSubStatus: string | null;
        toStatus: string | null;
        toSubStatus: string | null;
        notes: string | null;
        reason: string | null;
        createdBy: string | null;
        createdAt: string;
      }>;
      total: number;
    }>
  > {
    if (USE_MOCK_DATA) {
      return {
        success: true,
        data: {
          taskId: id,
          history: [],
          total: 0,
        },
      };
    }

    return apiClient.get(`/tasks/${id}/history`);
  },

  /**
   * Complete alta (registration) with insurer
   * POST /api/tasks-fsm/:id/complete-alta
   * Transitions task from DETENIDOS/alta to VERIFICACION/verificacion
   * @param data.uploadFolio - Optional folio/confirmation number from the alta process
   * @param data.altaCompletedDate - Optional date when the alta was completed (defaults to now)
   */
  async completeAlta(
    id: string,
    data?: { uploadFolio?: string; altaCompletedDate?: string }
  ): Promise<ApiResponse<Task>> {
    if (USE_MOCK_DATA) {
      // Mock implementation - simulate completion
      const task = await mockGetTask(id);
      return {
        success: true,
        data: {
          ...task,
          status: TaskStatus.VERIFICACION,
          subStatus: TaskSubStatus.VERIFICACION,
          requiresAlta: true,
          altaStatus: 'completed',
          altaCompletedDate: data?.altaCompletedDate
            ? new Date(data.altaCompletedDate)
            : new Date(),
          uploadFolio: data?.uploadFolio || null,
        } as Task,
      };
    }

    return apiClient.post(`/tasks-fsm/${id}/complete-alta`, data);
  },

  /**
   * Bulk transition multiple tasks to a target status
   * POST /api/tasks-fsm/bulk-transition
   * @param taskIds - Array of task IDs to transition
   * @param targetStatus - Target status for all tasks
   * @param targetSubStatus - Optional target sub-status
   * @param options - Optional configuration for the bulk operation
   */
  async bulkTransition(
    taskIds: string[],
    targetStatus: TaskStatus,
    targetSubStatus?: TaskSubStatus | null,
    options?: {
      stopOnError?: boolean;
      reason?: string;
      notes?: string;
    }
  ): Promise<ApiResponse<BulkTransitionResult>> {
    // Validate that we have tasks to process
    if (!taskIds || taskIds.length === 0) {
      throw new Error('No se seleccionaron tareas para la operación masiva');
    }

    if (USE_MOCK_DATA) {
      // Mock implementation - simulate bulk transition
      return {
        success: true,
        data: {
          totalRequested: taskIds.length,
          successful: taskIds.length,
          failed: 0,
          results: taskIds.map((taskId) => ({
            taskId,
            success: true,
            previousStatus: 'verificacion',
            previousSubStatus: null,
            newStatus: targetStatus,
            newSubStatus: targetSubStatus || null,
          })),
          rolledBack: false,
          message: `Successfully transitioned ${taskIds.length} tasks`,
        },
      };
    }

    // Accept 400 responses because the bulk endpoint returns detailed per-task results
    // even when some/all transitions fail (stopOnError). Without this, axios throws and
    // the detailed error info is lost, showing only "Request failed with status code 400".
    return apiClient.post(
      '/tasks-fsm/bulk-transition',
      {
        taskIds,
        targetStatus,
        targetSubStatus,
        ...options,
      },
      {
        validateStatus: (status) => status === 200 || status === 400,
      }
    );
  },

  /**
   * Bulk update commission status for multiple tasks
   * PATCH /api/tasks/bulk-commission-status
   * @param taskIds - Array of task IDs to update
   * @param commissionStatus - Target commission status
   * @param options - Optional configuration including commissionPaidDate for 'pagada' status
   */
  async bulkUpdateCommissionStatus(
    taskIds: string[],
    commissionStatus: CommissionStatus,
    options?: {
      reason?: string;
      /** Date when commission was paid (required for 'pagada' status) */
      commissionPaidDate?: string;
    }
  ): Promise<ApiResponse<BulkCommissionResult>> {
    // Validate that we have tasks to process
    if (!taskIds || taskIds.length === 0) {
      throw new Error('No se seleccionaron tareas para actualizar comisiones');
    }

    if (USE_MOCK_DATA) {
      // Mock implementation
      return {
        success: true,
        data: {
          totalRequested: taskIds.length,
          successful: taskIds.length,
          failed: 0,
          results: taskIds.map((taskId) => ({
            taskId,
            success: true,
            previousStatus: 'pendiente',
            newStatus: commissionStatus,
          })),
          message: `Successfully updated commission status for ${taskIds.length} tasks`,
        },
      };
    }

    return apiClient.patch('/tasks/bulk-commission-status', {
      taskIds,
      commissionStatus,
      ...options,
    });
  },

  /**
   * Toggle alta requirement for a task
   * PATCH /api/tasks/:id/alta
   * @param taskId - Task ID to toggle alta on
   * @param requiresAlta - Whether alta should be enabled
   * @returns Updated task with new commission amount
   */
  async toggleAlta(taskId: string, requiresAlta: boolean): Promise<ApiResponse<Task>> {
    if (USE_MOCK_DATA) {
      // Mock implementation
      const mockTask = await mockGetTask(taskId);
      if (!mockTask) {
        throw new Error('Task not found');
      }
      return {
        success: true,
        data: {
          ...mockTask,
          requiresAlta,
          altaFee: requiresAlta ? '350.00' : null,
          altaStatus: requiresAlta ? 'pending' : null,
        },
      };
    }

    return apiClient.patch(`/tasks/${taskId}/alta`, { requiresAlta });
  },
};

/**
 * Bulk Transition Result
 * Response structure from bulk transition endpoint
 */
export interface BulkTransitionResult {
  totalRequested: number;
  successful: number;
  failed: number;
  results: Array<{
    taskId: string;
    success: boolean;
    error?: string;
    previousStatus?: string;
    previousSubStatus?: string | null;
    newStatus?: string;
    newSubStatus?: string | null;
  }>;
  rolledBack: boolean;
  message: string;
}

/**
 * Bulk Commission Status Result
 * Response structure from bulk commission status endpoint
 */
export interface BulkCommissionResult {
  totalRequested: number;
  successful: number;
  failed: number;
  results: Array<{
    taskId: string;
    success: boolean;
    error?: string;
    previousStatus?: string;
    newStatus?: string;
  }>;
  message: string;
}
